import mixpanel from 'mixpanel-browser'

import { User } from 'src/models/User'
import { SessionPhase, isJobLive, resolveSessionPhase, TranscriptMetadata } from 'src/models/TranscriptMetadata'
import { getAwsRegion, getEnv, getMixpanelApiKey, isZoomAppsEnv } from 'src/utils/env'
import { AnalyticsMandatoryEventFields, AnalyticsTrackSearchDto } from 'src/models/Analytics'

import { ServiceBase } from './ServiceBase'

export type PageVisitContext = {
    user: User | null
    metadata: TranscriptMetadata
}

class AnalyticsService extends ServiceBase {
    private mandatoryEventFields: AnalyticsMandatoryEventFields = {
        appName: null,
        customerId: null,
        customerName: null,
        jobType: null,
        viewMode: null,
        orderId: null,
        env: null,
        isGuest: null,
        isZoomAppsEnv: null,
        email: null,
        isDownloadButtonVisible: null,
        visitTime: null,
        currentSessionPhase: null,
        isAiPanelAvailable: null,
    }

    private _metadataSet = false
    private _pageVisitContextSet = false
    sessionURL: string | null = null

    get areMandatoryFieldsSet() {
        return this._metadataSet && this._pageVisitContextSet
    }

    constructor() {
        super({ blockedEnvs: ['dev'] })
    }

    init() {
        const isEuRegion = getAwsRegion()?.startsWith('eu')

        mixpanel.init(getMixpanelApiKey()!, {
            persistence: 'localStorage',
            disable_cookie: true,
            cross_subdomain_cookie: false,
            ...(isEuRegion ? { api_host: 'https://api-eu.mixpanel.com' } : {}),
        })
    }

    identify(id: string) {
        mixpanel.identify(id)
    }

    track(event: string, props: Record<string, unknown>) {
        mixpanel.track(event, {
            sessionURL: this.sessionURL,
            ...props,
            ...this.mandatoryEventFields,
        })
    }

    updateUserProps({ userName, email, companyName, ...userProps }: User) {
        mixpanel.people.set({
            $name: userName,
            $email: email,
            $companyName: companyName,
            ...userProps,
        })
    }

    setSessionURL(sessionURL: string) {
        this.sessionURL = sessionURL
    }

    setMandatoryEventFields(metadata: TranscriptMetadata) {
        const jobType = metadata.viewMode === 'document' ? 'existing_transcript' : isJobLive(metadata) ? 'live' : 'post-production'

        this.mandatoryEventFields = {
            ...this.mandatoryEventFields,
            appName: 'Verbitext',
            customerId: metadata.customerId,
            customerName: metadata.customerName,
            jobType,
            viewMode: metadata.viewMode,
            orderId: metadata.orderId,
            env: getEnv(),
        }
        this._metadataSet = true
    }

    setPageVisitContext(context: PageVisitContext) {
        const zoom = isZoomAppsEnv()
        const { metadata, user } = context
        const sessionPhase = resolveSessionPhase(metadata)

        this.mandatoryEventFields = {
            ...this.mandatoryEventFields,
            isGuest: !user,
            isZoomAppsEnv: zoom,
            email: user?.email ?? null,
            isDownloadButtonVisible: !zoom && metadata.downloadable,
            visitTime: sessionPhase,
            currentSessionPhase: sessionPhase,
            isAiPanelAvailable: metadata.generativeAiEnabled,
        }
        this._pageVisitContextSet = true
    }

    setCurrentSessionPhase(phase: SessionPhase) {
        this.mandatoryEventFields = {
            ...this.mandatoryEventFields,
            currentSessionPhase: phase,
        }
    }

    trackSearch({ user, query }: AnalyticsTrackSearchDto) {
        this.track('Verbitext 2.0 - user search for terms', {
            email: user?.email,
            searchTerm: query,
        })
    }

    trackDownloadTranscript(sessionId: number | undefined) {
        this.track('Verbitext 2.0 - user downloaded transcript', {
            $sessionId: sessionId,
        })
    }
}

export const analyticsService = new AnalyticsService()
